import React, { memo, useMemo, useState } from 'react';
import {
	FieldErrors,
	FieldValues,
	UseFormGetValues,
	UseFormHandleSubmit,
	UseFormRegister,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';
import cn from 'classnames';

import { Button, CollapsibleBlock } from '@components/common';
import Status from '@components/ModalComponents/AssetForm/Attributes/Status';

import { getIsRequiredField } from '@utils/assetForm';

import { IAssetFormValues, TStatusOptionEnum } from '@interfaces/components/form';

import { ETabStatuses } from '@enums/asset';

import {
	assetStatusOptions,
	backhaulStatusOptions,
	booleanOptions,
	fieldsToTestAttributes,
	powerStatusOptions,
	tabsNames,
} from '../constants';

import styles from './Attributes.module.scss';

interface IAttributesProps {
	isEditMode: boolean;
	saveValues: (values: IAssetFormValues) => void;
	setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
	submitForm: (values: IAssetFormValues) => void;
	register: UseFormRegister<FieldValues>;
	handleSubmit: UseFormHandleSubmit<FieldValues>;
	errors: FieldErrors;
	setValue: UseFormSetValue<FieldValues>;
	trigger: UseFormTrigger<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	getValues: UseFormGetValues<FieldValues>;
	requiredFields?: string[] | null;
}

const Attributes = ({
	isEditMode,
	saveValues,
	setCurrentTab,
	submitForm,
	register,
	handleSubmit,
	errors,
	setValue,
	trigger,
	watch,
	getValues,
	requiredFields,
}: IAttributesProps) => {
	const backhaulAccessibilityStatus = watch('backhaulAccessibilityStatus');
	const powerSupplyAvailabilityStatus = watch('powerSupplyAvailabilityStatus');
	const assetSiteAvailabilityStatus = watch('assetSiteAvailabilityStatus');
	const isAvailable = watch('isAvailable');
	const specialImplementationIssuesExist = watch('specialImplementationIssuesExist');
	const backhaulAccessibilityDetailsFileIds = watch('backhaulAccessibilityDetailsFileIds');
	const assetSiteAvailabilityDetailsFileIds = watch('assetSiteAvailabilityDetailsFileIds');
	const powerSupplyAvailabilityDetailsFileIds = watch('powerSupplyAvailabilityDetailsFileIds');
	const specialImplementationIssueDetailsFileIds = watch('specialImplementationIssueDetailsFileIds');
	const assetAccessDetailsFileIds = watch('assetAccessDetailsFileIds');

	const tabs = useMemo(() => [
		{
			name: 'Backhaul accessibility status',
			id: 'backhaulAccessibility',
			type: ETabStatuses.status,
			options: backhaulStatusOptions,
			detailsName: 'backhaulAccessibilityDetails',
			radioName: 'backhaulAccessibilityStatus',
			errorMessage: errors?.backhaulAccessibilityDetails?.message,
			radioValue: backhaulAccessibilityStatus,
			fileFieldName: 'backhaulAccessibilityDetailsFileIds',
			files: backhaulAccessibilityDetailsFileIds,
			required: getIsRequiredField({ fieldName: 'backhaulAccessibilityDetails', requiredFields }),
		},
		{
			name: 'Power supply availability status',
			id: 'powerSupplyAvailability',
			type: ETabStatuses.status,
			options: powerStatusOptions,
			detailsName: 'powerSupplyAvailabilityDetails',
			radioName: 'powerSupplyAvailabilityStatus',
			errorMessage: errors?.powerSupplyAvailabilityDetails?.message,
			radioValue: powerSupplyAvailabilityStatus,
			fileFieldName: 'powerSupplyAvailabilityDetailsFileIds',
			files: powerSupplyAvailabilityDetailsFileIds,
			required: getIsRequiredField({ fieldName: 'powerSupplyAvailabilityDetails', requiredFields }),
		},
		{
			name: 'Asset site availability status',
			id: 'assetSiteAvailability',
			type: ETabStatuses.status,
			options: assetStatusOptions,
			detailsName: 'assetSiteAvailabilityDetails',
			radioName: 'assetSiteAvailabilityStatus',
			errorMessage: errors?.assetSiteAvailabilityDetails?.message,
			radioValue: assetSiteAvailabilityStatus,
			fileFieldName: 'assetSiteAvailabilityDetailsFileIds',
			files: assetSiteAvailabilityDetailsFileIds,
			required: getIsRequiredField({ fieldName: 'assetSiteAvailabilityDetails', requiredFields }),
		},
		{
			name: 'Asset site access availability',
			id: 'assetAccess',
			type: ETabStatuses.exist,
			options: booleanOptions,
			detailsName: 'assetAccessDetails',
			radioName: 'isAvailable',
			errorMessage: errors?.assetAccessDetails?.message,
			radioValue: isAvailable,
			fileFieldName: 'assetAccessDetailsFileIds',
			files: assetAccessDetailsFileIds,
			required: getIsRequiredField({ fieldName: 'assetAccessDetails', requiredFields }),
		},
		{
			name: 'Special implementation issues exist',
			id: 'specialImplementationIssue',
			type: ETabStatuses.exist,
			options: booleanOptions,
			detailsName: 'specialImplementationIssueDetails',
			radioName: 'specialImplementationIssuesExist',
			errorMessage: errors?.specialImplementationIssueDetails?.message,
			radioValue: specialImplementationIssuesExist,
			fileFieldName: 'specialImplementationIssueDetailsFileIds',
			files: specialImplementationIssueDetailsFileIds,
			required: getIsRequiredField({ fieldName: 'specialImplementationIssueDetails', requiredFields }),
		},
	], [
		assetAccessDetailsFileIds,
		assetSiteAvailabilityDetailsFileIds,
		assetSiteAvailabilityStatus,
		backhaulAccessibilityDetailsFileIds,
		backhaulAccessibilityStatus,
		errors?.assetAccessDetails?.message,
		errors?.assetSiteAvailabilityDetails?.message,
		errors?.backhaulAccessibilityDetails?.message,
		errors?.powerSupplyAvailabilityDetails?.message,
		errors?.specialImplementationIssueDetails?.message,
		isAvailable,
		powerSupplyAvailabilityDetailsFileIds,
		powerSupplyAvailabilityStatus,
		requiredFields,
		specialImplementationIssueDetailsFileIds,
		specialImplementationIssuesExist,
	]);

	const getCollapsibleBlockNames = () => (
		tabs.reduce<Record<string, boolean>>((acc, curr, index) => {
			acc[curr.name] = index === 0;

			return acc;
		}, {})
	);

	const [collapsibleBlocksState, setCollapsibleBlocksState] = useState<Record<string, boolean>>(
		() => getCollapsibleBlockNames());

	const toggleCollapsibleBlocks = (name: string) => {
		setCollapsibleBlocksState(prevState => {
			const allClosedState: Record<string, boolean> = {};

			for (const key in prevState) {
				allClosedState[key] = false;
			}

			return ({
				...allClosedState,
				[name]: !prevState[name],
			});
		});
	};

	const onSubmit = async (values: IAssetFormValues) => {
		const isValid = await trigger(fieldsToTestAttributes);

		if (!isValid) return;

		saveValues(values);
		submitForm(values);
	};

	const onClickBackHandler = async () => {
		const isValid = await trigger(fieldsToTestAttributes);

		if (!isValid) return;

		saveValues(getValues());
		setCurrentTab(tabsNames.address);
	};

	return (
		<div className={styles.attributesFormContainer}>
			{tabs.map(({
				name,
				id,
				type,
				options,
				detailsName,
				radioName,
				errorMessage,
				radioValue,
				fileFieldName,
				files,
				required,
			}) => {
				const collapsibleClassname = cn(styles.attribute, styles.collapsibleBlock);
				const isOpen = collapsibleBlocksState[name];

				return (
					<div key={id}>
						<div className={collapsibleClassname}>
							<CollapsibleBlock
								isOpen={isOpen}
								label={
									<div className={cn(styles.blockTitle, { [styles.error]: !!errorMessage })}>
										{name}
									</div>
								}
								name={name}
								toggleState={() => toggleCollapsibleBlocks(name)}
							>
								<Status
									detailsName={detailsName}
									errorMessage={errorMessage as string}
									fileFieldName={fileFieldName}
									files={files}
									isStatusType={type === ETabStatuses.status}
									radioName={radioName}
									radioValue={radioValue}
									register={register}
									required={required}
									setValue={setValue}
									statusOptions={options as Array<TStatusOptionEnum>}
								/>
							</CollapsibleBlock>
						</div>
					</div>
				);
			})}
			{!isEditMode
				&& <div className={styles.buttons}>
					<Button
						onClick={onClickBackHandler}
						text='Back'
						withoutBackground
					/>
					<Button
						onClick={handleSubmit(onSubmit)}
						text='Add'
					/>
				</div>
			}
		</div>
	);
};

export default memo(Attributes);
